import { Button, CircularProgress, Tooltip } from '@mui/material';
import { ButtonHTMLAttributes, ReactNode } from 'react';
import { useNullableModalProvider } from './ModalContext';

import styles from './ApplicationModal.module.css';

interface SubmitButtonProps {
	/** Submit handler  */
	submit?: () => void;
	/** When provided, will use button type submit and the given form name */
	formName?: string;
	/** Hovering the save button with pending changes will display this parameters */
	submitMessage?: string;
	isSubmitting?: boolean;
	isDisabled?: boolean;
	disabledMessage?: ReactNode;
	buttonText?: string;
	useSecondaryColor?: boolean;
	startAdornment?: React.ReactNode;
	className?: string;
}

/**
 * Submit button component for application modals to keep consistent behaviors.
 */
export default function SubmitButton(props: SubmitButtonProps) {
	const { submit, isDisabled, disabledMessage } = props;
	const modalContext = useNullableModalProvider();

	/**
	 * Handles the click event for the submit button.
	 * Calls the provided submit function if available and resets the modal context.
	 */
	function onSubmitClick() {
		if (submit) {
			submit();
		}

		modalContext?.reset();
	}

	const submitFormProps: Pick<ButtonHTMLAttributes<HTMLButtonElement>, 'form' | 'type'> = {
		form: props.formName,
		type: 'submit'
	};

	return (
		<Tooltip title={isDisabled ? disabledMessage : props.submitMessage ? props.submitMessage : 'Save and Close'}>
			<span>
				<Button
					variant='contained'
					color={props.useSecondaryColor ? 'secondary' : 'primary'}
					onClick={onSubmitClick}
					disabled={isDisabled || props.isSubmitting}
					className={props.className}
					startIcon={props.startAdornment}
					{...(props.formName ? submitFormProps : {})}
				>
					{!props.isSubmitting ? (
						props.buttonText ? (
							props.buttonText
						) : (
							'Save'
						)
					) : (
						<div className={styles['submit-btn-spinner']}>
							<CircularProgress size={20} />
						</div>
					)}
				</Button>
			</span>
		</Tooltip>
	);
}
